import { useMemo, useState } from "react";

import { ViewState } from "@devexpress/dx-react-scheduler";
import {
	AllDayPanel,
	AppointmentTooltip,
	Appointments,
	CurrentTimeIndicator,
	DateNavigator,
	DayView,
	MonthView,
	Scheduler,
	Toolbar,
	WeekView,
} from "@devexpress/dx-react-scheduler-material-ui";
import { IconButton, Tooltip, Typography } from "@mui/material";
import {
	FaRegCalendar,
	FaRegClock,
	FaRegTrashCan,
	FaUser,
} from "react-icons/fa6";
import { IoClose, IoPencil } from "react-icons/io5";
import { SlNotebook } from "react-icons/sl";
import { Link } from "react-router-dom";

import { TAvailability, TMyClass } from "../../../@types";
import { defaultTimezone } from "../../../constants/timezone";
import { useAccount } from "../../../hooks/useAccount";
import { formatTimeRange } from "../../../utils";
import { ExternalViewSwitcher } from "./ExternalViewSwitcher";

import "./schedule_calendar.scss";

export type TScheduleCalendarProps = {
	data: any[];
	onClickReschedule?: (myClass: TMyClass) => void;
	onCellDoubleClick?: (startDate: Date, endDate: Date) => void;
	onClickDeleteBusyTime?: (id: number) => void;
	showAvailability?: boolean;
	availableHours: TAvailability[];
};

export function ScheduleCalendar({
	data,
	onClickReschedule,
	onCellDoubleClick,
	onClickDeleteBusyTime,
	showAvailability,
	availableHours = [],
}: TScheduleCalendarProps) {
	const [currentViewName, setCurrentViewName] = useState<
		"Day" | "Week" | "Month"
	>("Week");
	const { account } = useAccount();

	const allAvailability = useMemo(() => {
		if (showAvailability && availableHours && availableHours.length > 0) {
			const availability = Array.from({ length: 7 }).fill(
				Array.from({ length: 24 * 4 }).fill(false),
			) as boolean[][];

			for (let i = 0; i < availableHours.length; i++) {
				const start =
					availableHours[i].startHour * 4 + availableHours[i].startMinute / 15;
				let end =
					availableHours[i].endHour * 4 + availableHours[i].endMinute / 15;
				if (end === 0) {
					end = 24 * 4;
				}

				for (let j = start; j < end; j++) {
					const newAvailabilityOfDay = [
						...availability[availableHours[i].dayOfWeek],
					];
					newAvailabilityOfDay[j] = true;
					availability[availableHours[i].dayOfWeek] = [...newAvailabilityOfDay];
				}
			}

			return availability;
		}

		return;
	}, [availableHours, showAvailability]);

	return (
		<div className="my-orka-schedule">
			<Scheduler height={600} data={data}>
				<ExternalViewSwitcher
					currentViewName={currentViewName}
					setCurrentViewName={setCurrentViewName}
				/>
				<ViewState
					currentViewName={currentViewName}
					defaultCurrentDate={new Date()}
				/>
				<DayView
					startDayHour={0}
					endDayHour={24}
					cellDuration={60}
					timeTableCellComponent={({ children, onDoubleClick, ...props }) => {
						if (!props.startDate || !props.endDate) {
							return null;
						}

						if (showAvailability && availableHours && allAvailability) {
							const dayOfWeek = props.startDate.getDay();
							const start = props.startDate.getHours() * 4;
							const availabilityOfThisBlock = allAvailability[dayOfWeek].slice(
								start,
								start + 4,
							);
							const calculatedAvailability = [];
							let temp;
							for (let i = 0; i < availabilityOfThisBlock.length; i++) {
								if (availabilityOfThisBlock[i] !== temp) {
									if (availabilityOfThisBlock[i]) {
										calculatedAvailability.push(1);
									} else {
										calculatedAvailability.push(-1);
									}
									temp = availabilityOfThisBlock[i];
								} else {
									if (availabilityOfThisBlock[i]) {
										calculatedAvailability[calculatedAvailability.length - 1]++;
									} else {
										calculatedAvailability[calculatedAvailability.length - 1]--;
									}
								}
							}

							return (
								<DayView.TimeTableCell
									{...props}
									onDoubleClick={(e) => {
										if (onCellDoubleClick) {
											if (props.startDate && props.endDate) {
												onCellDoubleClick(props.startDate, props.endDate);
											}
										} else {
											onDoubleClick?.(e);
										}
									}}>
									<div className="custom-cell">
										{calculatedAvailability.map((item, index) => {
											let className = "custom-cell-item-1";
											if (item < 0) {
												className = "custom-cell-item-2";
											}
											return (
												<div
													key={index}
													className={className}
													style={{ flex: Math.abs(item) }}
												/>
											);
										})}
									</div>
								</DayView.TimeTableCell>
							);
						}

						return (
							<DayView.TimeTableCell
								{...props}
								onDoubleClick={(e) => {
									if (onCellDoubleClick) {
										if (props.startDate && props.endDate) {
											onCellDoubleClick(props.startDate, props.endDate);
										}
									} else {
										onDoubleClick?.(e);
									}
								}}>
								<div className="custom-cell" />
							</DayView.TimeTableCell>
						);
					}}
				/>
				<WeekView
					startDayHour={0}
					endDayHour={24}
					cellDuration={60}
					timeTableCellComponent={({ children, onDoubleClick, ...props }) => {
						if (!props.startDate || !props.endDate) {
							return null;
						}

						if (showAvailability && availableHours && allAvailability) {
							const dayOfWeek = props.startDate.getDay();
							const start = props.startDate.getHours() * 4;
							const availabilityOfThisBlock = allAvailability[dayOfWeek].slice(
								start,
								start + 4,
							);
							const calculatedAvailability = [];
							let temp;
							for (let i = 0; i < availabilityOfThisBlock.length; i++) {
								if (availabilityOfThisBlock[i] !== temp) {
									if (availabilityOfThisBlock[i]) {
										calculatedAvailability.push(1);
									} else {
										calculatedAvailability.push(-1);
									}
									temp = availabilityOfThisBlock[i];
								} else {
									if (availabilityOfThisBlock[i]) {
										calculatedAvailability[calculatedAvailability.length - 1]++;
									} else {
										calculatedAvailability[calculatedAvailability.length - 1]--;
									}
								}
							}

							return (
								<WeekView.TimeTableCell
									{...props}
									onDoubleClick={(e) => {
										if (onCellDoubleClick) {
											if (props.startDate && props.endDate) {
												onCellDoubleClick(props.startDate, props.endDate);
											}
										} else {
											onDoubleClick?.(e);
										}
									}}>
									<div className="custom-cell">
										{calculatedAvailability.map((item, index) => {
											let className = "custom-cell-item-1";
											if (item < 0) {
												className = "custom-cell-item-2";
											}
											return (
												<div
													key={index}
													className={className}
													style={{ flex: Math.abs(item) }}
												/>
											);
										})}
									</div>
								</WeekView.TimeTableCell>
							);
						}

						return (
							<WeekView.TimeTableCell
								{...props}
								onDoubleClick={(e) => {
									if (onCellDoubleClick) {
										if (props.startDate && props.endDate) {
											onCellDoubleClick(props.startDate, props.endDate);
										}
									} else {
										onDoubleClick?.(e);
									}
								}}>
								<div className="custom-cell" />
							</WeekView.TimeTableCell>
						);
					}}
				/>
				<Toolbar />
				<DateNavigator />
				<MonthView
					timeTableCellComponent={({ onDoubleClick, ...props }) => {
						return (
							<MonthView.TimeTableCell
								{...props}
								onDoubleClick={(e) => {
									if (onCellDoubleClick) {
										if (props.startDate && props.endDate) {
											onCellDoubleClick(props.startDate, props.endDate);
										}
									} else {
										onDoubleClick?.(e);
									}
								}}></MonthView.TimeTableCell>
						);
					}}
				/>
				<AllDayPanel />
				<Appointments
					appointmentContentComponent={({ data }) => {
						if (!data.startDate || !data.endDate) {
							return null;
						}

						const { timeText } = formatTimeRange(
							data.startDate,
							new Date(new Date(data.endDate).getTime() + 1),
						);

						if (data.isMyBusyTime) {
							return (
								<div style={{ padding: 4 }}>
									<Typography className="appointment-text">
										{timeText}
									</Typography>
									<Typography className="appointment-text">Blocked</Typography>
								</div>
							);
						}

						return (
							<div style={{ padding: 4 }}>
								<Typography className="appointment-text">{timeText}</Typography>
								<Typography className="appointment-text">{`${data.user.firstName} ${data.user.lastName}`}</Typography>
								<Typography className="appointment-text">
									{data.lesson.title}
								</Typography>
							</div>
						);
					}}
					appointmentComponent={({ data, children, ...props }) => {
						let backgroundColor = "#1976E2";
						if (data.isMyBusyTime) {
							backgroundColor = "#F7C26D";
						} else if (data.isInstructor) {
							backgroundColor = "#64949A";
						}
						return (
							<Appointments.Appointment
								style={{ backgroundColor }}
								data={data}
								{...props}>
								{children}
							</Appointments.Appointment>
						);
					}}
				/>
				<CurrentTimeIndicator updateInterval={10000} />
				<AppointmentTooltip
					headerComponent={({ onHide, appointmentData }) => {
						if (!appointmentData) {
							return null;
						}

						if (appointmentData.isMyBusyTime) {
							return (
								<div className="tooltip-header">
									{!!onClickDeleteBusyTime && (
										<Tooltip title="Delete">
											<IconButton
												className="icon-button"
												onClick={() => {
													if (appointmentData.id) {
														onClickDeleteBusyTime(
															Number(
																appointmentData.id.toString().split("-")[0],
															),
														);
													}
													onHide?.();
												}}>
												<FaRegTrashCan />
											</IconButton>
										</Tooltip>
									)}
									<Tooltip title="Close">
										<IconButton className="icon-button" onClick={onHide}>
											<IoClose />
										</IconButton>
									</Tooltip>
								</div>
							);
						}

						return (
							<div className="tooltip-header">
								{appointmentData.canReschedule && (
									<Tooltip title="Reschedule">
										<IconButton
											onClick={() => {
												onClickReschedule?.(appointmentData as any);
												onHide?.();
											}}>
											<IoPencil className="normal-icon" />
										</IconButton>
									</Tooltip>
								)}
								<Tooltip title="Close">
									<IconButton onClick={onHide}>
										<IoClose className="normal-icon" />
									</IconButton>
								</Tooltip>
							</div>
						);
					}}
					contentComponent={({ appointmentData }) => {
						if (
							!appointmentData ||
							!appointmentData.startDate ||
							!appointmentData.endDate
						) {
							return null;
						}

						const { dateText, timeText } = formatTimeRange(
							appointmentData.startDate,
							new Date(new Date(appointmentData.endDate).getTime() + 1),
						);

						if (appointmentData.isMyBusyTime) {
							return (
								<div className="tooltip-content">
									<div className="info-container">
										<div>
											<SlNotebook className="small-icon" />
										</div>
										<Typography className="normal-text bold">
											Blocked
										</Typography>
									</div>
									<div className="info-container">
										<div>
											<FaRegCalendar className="small-icon" />
										</div>
										<Typography className="normal-text">{dateText}</Typography>
									</div>
									<div className="info-container">
										<div>
											<FaRegClock className="small-icon" />
										</div>
										<Typography className="normal-text">{timeText}</Typography>
									</div>
								</div>
							);
						}

						const studentName = `${appointmentData.user.firstName} ${appointmentData.user.lastName}`;
						const teacherName = `${appointmentData.lesson.course.teacher.firstName} ${appointmentData.lesson.course.teacher.lastName}`;

						return (
							<div className="tooltip-content">
								<div className="info-container">
									<div>
										<SlNotebook className="small-icon" />
									</div>
									<Typography className="normal-text bold">
										{appointmentData.lesson.course.name}
									</Typography>
								</div>
								<div className="info-container">
									<div className="small-icon" />
									<Typography className="normal-text">
										{appointmentData.lesson.title}
									</Typography>
								</div>
								<div className="info-container">
									<div>
										<FaRegCalendar className="small-icon" />
									</div>
									<Typography className="normal-text text">
										{dateText}
									</Typography>
								</div>
								<div className="info-container">
									<div>
										<FaRegClock className="small-icon" />
									</div>
									<Typography className="normal-text text">
										{timeText}
									</Typography>
								</div>
								<div className="info-container">
									<div>
										<FaUser className="small-icon" />
									</div>
									<Typography className="normal-text text">
										{appointmentData.isInstructor && studentName}
										{!appointmentData.isInstructor && teacherName}
									</Typography>
								</div>
								{appointmentData.isInstructor && (
									<Link
										to={`/teaching/${appointmentData.lesson.course.courseSlug}/${appointmentData.user.id}?class=${appointmentData.id}`}>
										<Typography className="normal-text">
											Student Binder
										</Typography>
									</Link>
								)}
								{!appointmentData.isInstructor && (
									<Link
										to={`/learning/${appointmentData.lesson.course.courseSlug}?class=${appointmentData.id}`}>
										<Typography className="normal-text">
											Course Binder
										</Typography>
									</Link>
								)}
							</div>
						);
					}}
				/>
			</Scheduler>
			<Typography className="normal-text">
				{`${account?.timezoneName ?? defaultTimezone} Time`}
			</Typography>
		</div>
	);
}
